// GoalsTab — shows the child's current reward goal + points progress

export function GoalsTab({ user, data, onDataChange, settings }) {
  const myPoints  = data.points?.[user.id] || 0;
  const myGoalId  = data.goals?.[user.id] || null;
  const myGoal    = myGoalId ? (data.rewards || []).find(r => r.id === myGoalId) : null;
  const allRewards = [...(data.rewards || [])].sort((a, b) => a.cost - b.cost);

  function setGoal(rewardId) {
    const newGoals = { ...(data.goals || {}), [user.id]: rewardId === myGoalId ? null : rewardId };
    onDataChange({ ...data, goals: newGoals });
  }

  const mySessions = data.sessions.filter(s => s.userId === user.id);
  const today = new Date().toDateString();
  const todayPts = mySessions
    .filter(s => new Date(s.date).toDateString() === today)
    .reduce((a, s) => a + (s.ptsEarned || 0), 0);

  // Last 7 days points
  const weekPts = (() => {
    const cutoff = Date.now() - 7 * 24 * 60 * 60 * 1000;
    return mySessions
      .filter(s => new Date(s.date).getTime() >= cutoff)
      .reduce((a, s) => a + (s.ptsEarned || 0), 0);
  })();

  return (
    <div>
      {/* Points summary */}
      <div className="card" style={{ marginBottom: "1rem" }}>
        <div style={{ fontWeight: 700, fontSize: "1.1rem", fontFamily: "var(--heading-font, 'Nunito', sans-serif)", marginBottom: ".85rem", color: "var(--navy)" }}>⭐ Your Points</div>
        <div style={{ display: "flex", gap: "1.25rem", flexWrap: "wrap" }}>
          {[
            { val: myPoints, label: "total points", borderColor: "var(--blue)",  bg: "var(--blue-light)",  color: "var(--blue)"  },
            { val: todayPts, label: "today",         borderColor: "var(--green)", bg: "var(--green-light)", color: "var(--green)" },
            { val: weekPts,  label: "this week",     borderColor: "var(--amber)", bg: "var(--amber-light)", color: "var(--amber)" },
          ].map(({ val, label, borderColor, bg, color }) => (
            <div key={label} style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: ".35rem" }}>
              <div style={{
                width: 64, height: 64, borderRadius: "50%",
                background: bg, border: `3px dashed ${borderColor}`,
                display: "flex", alignItems: "center", justifyContent: "center",
              }}>
                <span style={{ fontFamily: "var(--heading-font, 'Nunito', sans-serif)", fontSize: 20, color, fontWeight: 800 }}>{val}</span>
              </div>
              <p style={{ fontFamily: "var(--heading-font, 'Nunito', sans-serif)", fontSize: 13, color: "var(--muted)", margin: 0 }}>{label}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Current goal */}
      <div className="card" style={{ marginBottom: "1rem" }}>
        <div style={{ fontWeight: 700, fontSize: "1.1rem", fontFamily: "var(--heading-font, 'Nunito', sans-serif)", marginBottom: ".75rem", color: "var(--navy)" }}>🎯 Current Goal</div>
        {myGoal ? (
          <>
            <div style={{ display: "flex", alignItems: "center", gap: ".75rem", marginBottom: ".75rem" }}>
              <span style={{ fontSize: 28 }}>{myGoal.emoji}</span>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 700, fontSize: ".9rem" }}>{myGoal.name}</div>
                <div style={{ fontSize: ".78rem", color: "var(--muted)" }}>⭐ {myPoints} / {myGoal.cost} pts</div>
              </div>
              {myPoints >= myGoal.cost && (
                <span style={{ background: "var(--green)", color: "#fff", fontSize: ".7rem", fontWeight: 700, padding: ".2rem .6rem", borderRadius: 999 }}>Ready! 🎉</span>
              )}
            </div>
            <div style={{ background: "var(--surface2)", borderRadius: 999, height: 12, overflow: "hidden", marginBottom: ".5rem" }}>
              <div style={{
                height: "100%",
                background: myPoints >= myGoal.cost ? "var(--green)" : "var(--blue)",
                borderRadius: 999,
                width: `${Math.min(100, Math.round((myPoints / myGoal.cost) * 100))}%`,
                transition: "width .5s",
              }} />
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: ".75rem", color: "var(--muted)" }}>
              <span>{Math.min(100, Math.round((myPoints / myGoal.cost) * 100))}% complete</span>
              {myPoints < myGoal.cost && <span>{myGoal.cost - myPoints} pts to go</span>}
            </div>
            {myPoints >= myGoal.cost && (
              <div style={{ marginTop: ".75rem", background: "var(--green-light)", border: "1.5px solid var(--green)", borderRadius: 10, padding: ".6rem .9rem", fontSize: ".8rem", color: "var(--green)", fontWeight: 700 }}>
                🎉 You've earned enough points! Head to the Rewards tab to request it.
              </div>
            )}
            <button
              onClick={() => setGoal(null)}
              style={{ marginTop: ".75rem", background: "none", border: "1px solid var(--border)", borderRadius: 8, padding: ".35rem .75rem", fontSize: ".78rem", color: "var(--muted)", cursor: "pointer" }}
            >
              Clear goal
            </button>
          </>
        ) : (
          <div style={{ color: "var(--muted)", fontSize: ".85rem", textAlign: "center", padding: "1rem 0" }}>
            <div style={{ fontSize: 32, marginBottom: ".5rem" }}>🎯</div>
            No goal set yet — pick one below!
          </div>
        )}
      </div>

      {/* Available rewards to set as goal */}
      {allRewards.length > 0 && (
        <div className="card">
          <div style={{ fontWeight: 700, fontSize: "1.1rem", fontFamily: "var(--heading-font, 'Nunito', sans-serif)", marginBottom: ".75rem", color: "var(--navy)" }}>🏆 Set a Goal</div>
          <div style={{ display: "flex", flexDirection: "column", gap: ".5rem" }}>
            {allRewards.map(r => {
              const isActive = myGoalId === r.id;
              const canAfford = myPoints >= r.cost;
              return (
                <div
                  key={r.id}
                  onClick={() => setGoal(r.id)}
                  style={{
                    display: "flex", alignItems: "center", gap: ".75rem",
                    background: isActive ? "var(--blue-light)" : "var(--surface2)",
                    border: isActive ? "1.5px solid var(--blue)" : "1.5px solid transparent",
                    borderRadius: 12, padding: ".65rem .85rem", cursor: "pointer",
                    transition: "all .15s",
                  }}
                >
                  <span style={{ fontSize: 22 }}>{r.emoji}</span>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 700, fontSize: ".85rem" }}>{r.name}</div>
                    <div style={{ fontSize: ".72rem", color: "var(--muted)" }}>⭐ {r.cost} pts</div>
                  </div>
                  {canAfford && (
                    <span style={{ background: "var(--green)", color: "#fff", fontSize: ".65rem", fontWeight: 700, padding: ".15rem .45rem", borderRadius: 999 }}>Affordable</span>
                  )}
                  {isActive && (
                    <span style={{ color: "var(--blue)", fontSize: ".75rem", fontWeight: 700 }}>✓ Active</span>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {allRewards.length === 0 && (
        <div className="card" style={{ textAlign: "center", color: "var(--muted)", padding: "1.5rem" }}>
          <div style={{ fontSize: 32, marginBottom: ".5rem" }}>🏆</div>
          No rewards set up yet — ask your parent to add some!
        </div>
      )}
    </div>
  );
}
